/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import BaseModal from 'src/components/ts-components/BaseModal'
import { TextInput } from 'src/components/ts-components/TextInput'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import SNButton from 'src/components/ts-components/SNButton'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  name?: string
  label?: string
  helperText?: string
  fieldErrors?: string[]
  customError?: any
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content...',
  label,
  name,
  helperText,
  fieldErrors,
  customError,
}) => {
  const quillRef = useRef<ReactQuill>(null)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkRange, setLinkRange] = useState<any>(null)
  const [showLinkTooltip, setShowLinkTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const [currentLinkUrl, setCurrentLinkUrl] = useState('')

  const hasError = name && fieldErrors?.includes(name)

  const handleCustomLink = useCallback(() => {
    const quill = quillRef.current?.getEditor()
    if (quill) {
      const range = quill.getSelection()
      if (range) {
        setLinkRange(range)

        // Check if there's already a link in the current selection
        const format = quill.getFormat(range)
        const existingLink = format.link || ''

        setLinkUrl(existingLink)
        setShowLinkModal(true)
      }
    }
  }, [])

  // Determine if we're editing an existing link
  const isEditingLink = linkUrl.trim() !== ''
  const modalTitle = isEditingLink ? 'Edit Link' : 'Add Link'

  const handleLinkInsert = useCallback(() => {
    const quill = quillRef.current?.getEditor()
    if (quill && linkRange && linkUrl.trim()) {
      let finalUrl = linkUrl.trim()

      // Ensure URL has a protocol
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`
      }

      // Insert link at the saved range
      quill.setSelection(linkRange)
      quill.format('link', finalUrl)
    }
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkRange(null)
  }, [linkRange, linkUrl])

  const handleLinkCancel = useCallback(() => {
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkRange(null)
  }, [])

  const handleLinkRemove = useCallback(() => {
    const quill = quillRef.current?.getEditor()
    if (quill && linkRange) {
      quill.setSelection(linkRange)
      quill.format('link', false) // Remove the link format
    }
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkRange(null)
    setShowLinkTooltip(false)
  }, [linkRange])

  // Make toolbar buttons non-tabbable so Tab skips directly to the editor
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) {
      return
    }
    const toolbar = (quill as any).container
      ?.closest('.quill')
      ?.querySelector('.ql-toolbar')
    if (toolbar) {
      const focusables = toolbar.querySelectorAll(
        'button, select, .ql-picker-label, .ql-picker-item'
      )
      focusables.forEach((el: HTMLElement) => {
        el.setAttribute('tabindex', '-1')
      })
    }
  }, [])

  // Handle clicks on links to show tooltip
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) {
      return
    }

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const linkElement = target.closest('a')

      if (linkElement && linkElement.href) {
        e.preventDefault()
        e.stopPropagation()

        const rect = linkElement.getBoundingClientRect()

        setCurrentLinkUrl(linkElement.href)
        setTooltipPosition({
          top: rect.bottom + 5,
          left: rect.left,
        })
        setShowLinkTooltip(true)
        setShowLinkModal(false)
      } else {
        setShowLinkTooltip(false)
      }
    }

    const container = (quill as any).container as HTMLElement
    container.addEventListener('click', handleLinkClick)

    return () => {
      container.removeEventListener('click', handleLinkClick)
    }
  }, [])

  // Handle clicks outside tooltip to hide it
  useEffect(() => {
    if (!showLinkTooltip) {
      return
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.link-tooltip') && !target.closest('a')) {
        setShowLinkTooltip(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showLinkTooltip])

  const handleTooltipEdit = useCallback(() => {
    const quill = quillRef.current?.getEditor()
    if (quill && currentLinkUrl) {
      // Find the link element in the DOM
      const container = (quill as any).container as HTMLElement
      const links = container.querySelectorAll('a')

      for (const link of Array.from(links) as HTMLAnchorElement[]) {
        if (link.href === currentLinkUrl) {
          // Select the text of this link
          const range = document.createRange()
          range.selectNodeContents(link)

          // Convert DOM selection to Quill selection
          const selection = window.getSelection()
          if (selection) {
            selection.removeAllRanges()
            selection.addRange(range)

            // Get the Quill selection equivalent
            const quillSelection = quill.getSelection()
            if (quillSelection) {
              setLinkRange(quillSelection)
              setLinkUrl(currentLinkUrl)
              setShowLinkModal(true)
              setShowLinkTooltip(false)
            }
          }
          break
        }
      }
    }
  }, [currentLinkUrl])

  const handleTooltipRemove = useCallback(() => {
    const quill = quillRef.current?.getEditor()
    if (quill && currentLinkUrl) {
      const container = (quill as any).container as HTMLElement
      const links = container.querySelectorAll('a')

      for (const link of Array.from(links) as HTMLAnchorElement[]) {
        if (link.href === currentLinkUrl) {
          // Select the text of this link
          const range = document.createRange()
          range.selectNodeContents(link)

          const selection = window.getSelection()
          if (selection) {
            selection.removeAllRanges()
            selection.addRange(range)

            // Get the Quill selection and remove the link format
            const quillSelection = quill.getSelection()
            if (quillSelection) {
              quill.format('link', false)
            }
          }
          break
        }
      }
      setShowLinkTooltip(false)
    }
  }, [currentLinkUrl])

  const isValidUrl = (url: string) => {
    if (!url.trim()) {
      return false
    }
    const trimmedUrl = url.trim()

    try {
      new URL(trimmedUrl)
      return true
    } catch {
      // If it doesn't start with a protocol, try with http://
      try {
        new URL(`http://${trimmedUrl}`)
        return trimmedUrl.includes('.') && trimmedUrl.length > 3
      } catch {
        return false
      }
    }
  }

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link'],
          ['clean'],
        ],
        handlers: {
          link: handleCustomLink,
        },
      },
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: () => {
              const editor = quillRef.current?.getEditor()
              if (editor) {
                ;(editor as any).blur()
              }
              return false
            },
          },
        },
      },
    }),
    [handleCustomLink]
  )

  const formats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'bullet',
      'indent',
      'align',
      'link',
    ],
    []
  )

  // Create high-z-index modal styles that preserve all original styling
  const highZIndexModalStyles = useMemo(
    () => ({
      header: {
        backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
        paddingLeft: MODAL_TOKENS.PADDING_HEADER,
        paddingRight: MODAL_TOKENS.PADDING_HEADER,
      },
      body: {
        borderBottom: `1px solid ${MODAL_TOKENS.BORDER_COLOR}`,
        borderTop: `1px solid ${MODAL_TOKENS.BORDER_COLOR}`,
        backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
      },
      content: {
        backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
        paddingTop: '8px',
        paddingBottom: MODAL_TOKENS.PADDING_FOOTER,
        paddingLeft: '0',
        paddingRight: '0',
      },
      wrapper: {
        zIndex: 10020, // Higher than default 10002
      },
      mask: {
        backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
        backgroundColor: MODAL_TOKENS.MASK_COLOR,
        zIndex: 10019, // Higher than default 10001
      },
      footer: {
        paddingLeft: MODAL_TOKENS.PADDING_FOOTER,
        paddingRight: MODAL_TOKENS.PADDING_FOOTER,
      },
    }),
    []
  )

  // Custom modal actions with OK on left and Remove Link on right
  const modalActions = useMemo(
    () => (
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          gap: 16px;
          width: 100%;
          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <SNButton
          text='OK'
          onClick={handleLinkInsert}
          variant='primary'
          disabled={!isValidUrl(linkUrl)}
        />
        <SNButton
          text='Remove Link'
          onClick={handleLinkRemove}
          variant='danger'
          disabled={!isEditingLink}
        />
      </div>
    ),
    [isEditingLink, handleLinkRemove, handleLinkInsert, linkUrl]
  )

  // Normalize and clean Quill HTML output
  const sanitizeHtml = useCallback((html: string): string => {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html || '', 'text/html')
      const body = doc.body

      // Helper: normalize text content (remove nbsp/zwsp) and trim
      const normalizedText = (node: Node) =>
        (node.textContent || '')
          .replace(/\u00A0/g, ' ')
          .replace(/\u200B/g, '')
          .trim()

      // If overall content has no visible text, return empty string
      if (normalizedText(body) === '') {
        return ''
      }

      // Helper: check if an element node is visually empty
      const isVisuallyEmpty = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
          return normalizedText(node) === ''
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          // If it has no text (after normalization) and no non-empty descendants
          return normalizedText(el) === ''
        }
        return true
      }

      // Strip leading empty blocks like <p><br></p>
      // Keep a trailing empty block so pressing Enter creates a visible new line
      while (body.firstChild && isVisuallyEmpty(body.firstChild)) {
        body.removeChild(body.firstChild)
      }

      return body.innerHTML
    } catch {
      return html || ''
    }
  }, [])

  // Wrap ReactQuill onChange to emit cleaned HTML or empty string
  const handleChange = useCallback(
    (
      content: string,
      _delta: any,
      _source: any,
      editor: { getText: () => string }
    ) => {
      // Quill returns "\n" for an empty editor
      const plain = (editor?.getText?.() || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\u200B/g, '')
        .trim()

      if (plain.length === 0) {
        onChange('')
        return
      }

      const cleaned = sanitizeHtml(content)
      onChange(cleaned)
    },
    [onChange, sanitizeHtml]
  )

  return (
    <>
      {/* Link Tooltip */}
      {showLinkTooltip && (
        <div
          className='link-tooltip'
          css={css`
            position: fixed;
            top: ${tooltipPosition.top}px;
            left: ${tooltipPosition.left}px;
            background: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px;
            padding: 8px 12px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            z-index: 10030;
            display: flex;
            align-items: center;
            gap: 8px;
            max-width: 400px;
          `}
        >
          <a
            href={currentLinkUrl}
            target='_blank'
            rel='noopener noreferrer'
            css={css`
              color: ${colors.PRIMARY};
              text-decoration: none;
              font-size: 14px;
              flex: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              &:hover {
                text-decoration: underline;
              }
            `}
          >
            {currentLinkUrl}
          </a>
          <button
            onClick={handleTooltipEdit}
            css={css`
              background: ${colors.SECONDARY_200};
              border: 1px solid ${colors.SECONDARY_100};
              border-radius: 4px;
              padding: 4px 8px;
              font-size: 12px;
              color: ${colors.NEUTRAL_0};
              cursor: pointer;
              &:hover {
                background: ${colors.SECONDARY_100};
              }
            `}
          >
            Edit
          </button>
          <button
            onClick={handleTooltipRemove}
            css={css`
              background: transparent;
              border: none;
              padding: 4px;
              cursor: pointer;
              color: ${colors.NEUTRAL_50};
              font-size: 16px;
              &:hover {
                color: ${colors.ERROR_200};
              }
            `}
          >
            ×
          </button>
        </div>
      )}

      <BaseModal
        isOpen={showLinkModal}
        onClose={handleLinkCancel}
        title={modalTitle}
        actions={modalActions}
        styles={highZIndexModalStyles}
        width='500px'
      >
        <TextInput
          name='linkUrl'
          value={linkUrl}
          setValue={setLinkUrl}
          fieldErrors={[]}
          customError={null}
          label='Link URL'
          placeholder='Enter URL (e.g., https://example.com)'
          autoComplete='url'
        />
      </BaseModal>

      <div
        css={css`
          flex: 1 1 0;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 8px;
          display: inline-flex;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 2px;
          `}
        >
          {label && (
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
            >
              {label}
            </Text>
          )}
          {helperText && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {helperText}
            </Text>
          )}
        </div>
        <div
          css={css`
            width: 100%;
            position: relative;
            .ql-container {
              background: ${colors.SECONDARY_300};
              border-bottom-left-radius: 8px;
              border-bottom-right-radius: 8px;
              font-family: inherit;
              font-size: 14px;
              min-height: 200px;
            }
            .ql-editor {
              min-height: 200px;
              cursor: text;
            }
            .ql-toolbar {
              background: ${colors.SECONDARY_200};
              border-top-left-radius: 8px;
              border-top-right-radius: 8px;
              border-color: ${hasError
                ? colors.ERROR_LIGHT
                : colors.SECONDARY_100};
            }
            .ql-toolbar button {
              color: ${colors.NEUTRAL_0};
            }
            .ql-toolbar button:hover {
              color: ${colors.PRIMARY};
            }
            .ql-toolbar button.ql-active {
              color: ${colors.PRIMARY};
            }
            .ql-toolbar .ql-stroke {
              stroke: ${colors.NEUTRAL_0};
            }
            .ql-toolbar .ql-stroke:hover {
              stroke: ${colors.PRIMARY};
            }
            .ql-toolbar .ql-active .ql-stroke {
              stroke: ${colors.PRIMARY};
            }
            .ql-toolbar .ql-fill {
              fill: ${colors.NEUTRAL_0};
            }
            .ql-toolbar .ql-fill:hover {
              fill: ${colors.PRIMARY};
            }
            .ql-toolbar .ql-active .ql-fill {
              fill: ${colors.PRIMARY};
            }
            .ql-toolbar .ql-picker {
              color: ${colors.NEUTRAL_0};
            }
            .ql-toolbar .ql-picker-options {
              background: ${colors.SECONDARY_300};
            }
            .ql-editor {
              color: ${colors.NEUTRAL_0};
            }
            .ql-editor.ql-blank::before {
              color: ${colors.NEUTRAL_50};
              font-style: normal;
            }
            .ql-editor strong {
              font-weight: bold;
              color: ${colors.NEUTRAL_0};
            }
            .ql-editor em {
              font-style: italic;
              color: ${colors.NEUTRAL_0};
            }
            .ql-editor u {
              text-decoration: underline;
              color: ${colors.NEUTRAL_0};
            }
            .ql-editor s {
              text-decoration: line-through;
              color: ${colors.NEUTRAL_0};
            }
            .ql-snow .ql-editor pre.ql-syntax {
              background-color: ${colors.SECONDARY_400};
              color: ${colors.NEUTRAL_0};
            }
            .ql-container.ql-snow {
              border-color: ${hasError
                ? colors.ERROR_LIGHT
                : colors.SECONDARY_100};
            }
            .ql-editor a {
              cursor: pointer;
              color: ${colors.PRIMARY};
            }
            .ql-tooltip {
              display: none !important;
            }
          `}
        >
          <ReactQuill
            ref={quillRef}
            theme='snow'
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
            formats={formats}
            preserveWhitespace={true}
          />
        </div>
        {hasError && (
          <ErrorLabel
            fieldErrors={fieldErrors}
            customError={customError}
            name={name}
          />
        )}
      </div>
    </>
  )
}

export default RichTextEditor
